import { useState, useEffect } from "react";
import { Box, Button, Paper, Grid, CircularProgress, Alert, Typography } from "@mui/material";
import { ArrowBack } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { connect, ConnectedProps } from "react-redux";
import { bindActionCreators, Dispatch } from "redux";
import MenuLayout from "../app/MenuLayout";
import {
  CouponTemplateFormDto,
  CodeType,
  ActivationType,
  ValueType,
  CouponStatusAfterIssuance,
  DesignType,
  mapCouponTemplateFormToCreateRequest,
  CouponTemplateApiResponse,
  mapCouponTemplateApiResponseToForm,
  CouponTemplateStatus,
} from "../../store/models/coupon/CouponManagementDto";
import { Api } from "../../api/Api";
import { thunkCreateErrorNotification } from "../../store/actions/NotificationActions";
import { thunkUpdateCouponTemplate } from "../../store/actions/CouponActions";
import CouponBasicInfoStep from "./steps/CouponBasicInfoStep";
import CouponRewardValueStep from "./steps/CouponRewardValueStep";
import CouponTypeStep from "./steps/CouponTypeStep";
import CouponValidityStep from "./steps/CouponValidityStep";
import CouponOperationalUnitsStep from "./steps/CouponOperationalUnitsStep";
import CouponCodeGenerationStep from "./steps/CouponCodeGenerationStep";
import CouponDesignStep from "./steps/CouponDesignStep";

const mapDispatchToProps = (dispatch: Dispatch) =>
  bindActionCreators(
    {
      thunkCreateErrorNotification,
      thunkUpdateCouponTemplate,
    },
    dispatch
  );

const connector = connect(null, mapDispatchToProps);
type CouponEditPageProps = ConnectedProps<typeof connector>;

const CouponEditPage = ({ thunkCreateErrorNotification, thunkUpdateCouponTemplate }: CouponEditPageProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [originalStatus, setOriginalStatus] = useState<CouponTemplateStatus | null>(null);

  const [formData, setFormData] = useState<CouponTemplateFormDto>({
    name: "",
    // Reward and Value
    valueType: ValueType.PERCENTAGE,
    value: "",
    minimumPurchaseAmount: undefined,
    // Coupon Type
    activationType: ActivationType.FREE,
    requiredPoints: undefined,
    initialCouponStatus: CouponStatusAfterIssuance.ACTIVE,
    // Validity
    validityType: 'FIXED_DATE',
    validityPeriodDays: 30,
    // Redeemable At
    operationalUnitNumbers: [],
    // Code Generation
    codeType: CodeType.GENERIC,
    codePrefix: "",
    codeLength: undefined,
    couponCode: undefined,
    // Design
    designType: DesignType.NONE,
    displayedCouponName: undefined,
    description: undefined,
    couponImage: undefined,
    infoTextForEndUsers: undefined,
    // Legacy
    uploadFileName: "",
    eligibilityType: undefined,
    eligibilityNumbers: [],
  });

  // Load existing coupon data
  useEffect(() => {
    const loadCoupon = async () => {
      if (!id) {
        console.error("No coupon ID provided");
        thunkCreateErrorNotification("No coupon ID provided", new Error("Missing coupon ID"));
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const couponData = (await Api.getCouponTemplateByNumber(id)) as CouponTemplateApiResponse;
        const formDataFromApi = mapCouponTemplateApiResponseToForm(couponData);
        // Ensure initialCouponStatus has a value
        if (!formDataFromApi.initialCouponStatus) {
          formDataFromApi.initialCouponStatus = CouponStatusAfterIssuance.ACTIVE;
        }
        setFormData(formDataFromApi);
        // Extract status from the API response directly
        setOriginalStatus((couponData.status as CouponTemplateStatus) || CouponTemplateStatus.DRAFT);
      } catch (error) {
        console.error("Failed to load coupon:", error);
        thunkCreateErrorNotification("Failed to load coupon data", error);
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      loadCoupon();
    }
  }, [id, thunkCreateErrorNotification]);

  const handleCancel = () => {
    navigate(-1);
  };

  const isValidAlphanumeric = (value: string): boolean => {
    const alphanumericPattern = /^[a-zA-Z0-9]*$/;
    return alphanumericPattern.test(value);
  };

  const validateBasicFields = (formData: CouponTemplateFormDto): string[] => {
    const errors: string[] = [];

    if (!formData.name?.trim()) {
      errors.push("Coupon name is required");
    }

    if (!formData.validityPeriodDays || formData.validityPeriodDays <= 0) {
      errors.push("Validity period must be positive");
    }

    return errors;
  };

  const validateValueFields = (formData: CouponTemplateFormDto): string[] => {
    const errors: string[] = [];
    const requiresValue = [ValueType.PERCENTAGE, ValueType.FIXED_AMOUNT].includes(formData.valueType);

    if (requiresValue && !formData.value?.trim()) {
      errors.push("Coupon value is required for this value type");
    }

    return errors;
  };

  const validateCodeFields = (formData: CouponTemplateFormDto): string[] => {
    const errors: string[] = [];

    if (formData.codeType === CodeType.INDIVIDUAL_GENERATED) {
      if (formData.codePrefix && formData.codePrefix.trim() && !isValidAlphanumeric(formData.codePrefix)) {
        errors.push("Code prefix can only contain letters and numbers");
      }

      if (!formData.codeLength || formData.codeLength < 4 || formData.codeLength > 20) {
        errors.push("Code length must be between 4 and 20 characters");
      }
    } else if (formData.codeType === CodeType.GENERIC) {
      if (!formData.couponCode?.trim()) {
        errors.push("Generic code is required");
      } else if (!isValidAlphanumeric(formData.couponCode)) {
        errors.push("Coupon code can only contain letters and numbers");
      }
    }

    return errors;
  };

  const validateActivationFields = (formData: CouponTemplateFormDto): string[] => {
    const errors: string[] = [];

    if (
      formData.activationType === ActivationType.POINTS &&
      (!formData.requiredPoints || formData.requiredPoints <= 0)
    ) {
      errors.push("Required points must be positive when activation requires points");
    }

    return errors;
  };

  const validateFormData = (formData: CouponTemplateFormDto): string[] => {
    return [
      ...validateBasicFields(formData),
      ...validateValueFields(formData),
      ...validateCodeFields(formData),
      ...validateActivationFields(formData),
    ];
  };

  const handleSave = async () => {
    if (!id) {
      thunkCreateErrorNotification("No coupon ID available for update", new Error("Missing coupon ID"));
      return;
    }

    try {
      // Validate form data first
      const validationErrors = validateFormData(formData);
      if (validationErrors.length > 0) {
        thunkCreateErrorNotification("Validation Error", new Error(validationErrors.join(". ")));
        return;
      }

      setSaving(true);
        const couponRequest = mapCouponTemplateFormToCreateRequest(formData);
      // Add status to the request for update operations
      if (originalStatus) {
        couponRequest.status = originalStatus;
      }
      const success = await thunkUpdateCouponTemplate(id, couponRequest);

      if (success) {
        navigate(-1);
      }
    } catch (error) {
      console.error("Failed to update coupon:", error);
      thunkCreateErrorNotification("Failed to update coupon", error);
    } finally {
      setSaving(false);
    }
  };

  const updateFormData = (updates: Partial<CouponTemplateFormDto>) => {
    setFormData((prev) => ({ ...prev, ...updates }));
  };

  const isFieldEditable = (): boolean => {
    return originalStatus === CouponTemplateStatus.DRAFT;
  };

  const getReadOnlyMessage = () => {
    if (originalStatus === CouponTemplateStatus.ACTIVE) {
      return t(
        "coupon.editPage.activeReadOnlyMessage",
        "This coupon is ACTIVE and cannot be edited. Only DRAFT coupons can be modified."
      );
    }
    if (originalStatus === CouponTemplateStatus.INACTIVE) {
      return t(
        "coupon.editPage.inactiveReadOnlyMessage",
        "This coupon is INACTIVE and cannot be edited. Only DRAFT coupons can be modified."
      );
    }
    return "";
  };

  if (loading) {
    return (
      <MenuLayout headline={t("coupon.editPage.loadingCoupon", "Loading Coupon...")}>
        <Box display="flex" justifyContent="center" alignItems="center" height="300px">
          <CircularProgress />
        </Box>
      </MenuLayout>
    );
  }

  const readOnly = !isFieldEditable();

  return (
    <MenuLayout headline={`${t("coupon.editPage.editCouponTitle", "Edit Coupon")}: ${formData.name}`}>
      <Box sx={{ width: "100%" }}>
        <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
          <Button startIcon={<ArrowBack />} onClick={() => navigate("/loyalty/coupon")}>
            {t("coupon.editPage.backToCoupons", "Back to Coupons")}
          </Button>
        </Box>

        {readOnly && (
          <Alert severity={originalStatus === CouponTemplateStatus.ACTIVE ? "info" : "warning"} sx={{ mb: 3 }}>
            <Typography variant="body2">{getReadOnlyMessage()}</Typography>
          </Alert>
        )}

        <Grid container spacing={4}>
          {/* Coupon Name Section */}
          <Grid item xs={12}>
            <Paper sx={{ p: 3, borderRadius: 3, boxShadow: 3 }}>
              <CouponBasicInfoStep formData={formData} updateFormData={updateFormData} readOnly={readOnly} />
            </Paper>
          </Grid>

          {/* Reward and Value Section */}
          <Grid item xs={12}>
            <Paper sx={{ p: 3, borderRadius: 3, boxShadow: 3 }}>
              <CouponRewardValueStep formData={formData} updateFormData={updateFormData} readOnly={readOnly} />
            </Paper>
          </Grid>

          {/* Coupon Type Section */}
          <Grid item xs={12}>
            <Paper sx={{ p: 3, borderRadius: 3, boxShadow: 3 }}>
              <CouponTypeStep formData={formData} updateFormData={updateFormData} readOnly={readOnly} />
            </Paper>
          </Grid>

          {/* Validity Section */}
          <Grid item xs={12}>
            <Paper sx={{ p: 3, borderRadius: 3, boxShadow: 3 }}>
              <CouponValidityStep formData={formData} updateFormData={updateFormData} readOnly={readOnly} />
            </Paper>
          </Grid>

          {/* Redeemable At Section */}
          <Grid item xs={12}>
            <Paper sx={{ p: 3, borderRadius: 3, boxShadow: 3 }}>
              <CouponOperationalUnitsStep formData={formData} updateFormData={updateFormData} readOnly={readOnly} />
            </Paper>
          </Grid>

          {/* Code Generation Section */}
          <Grid item xs={12}>
            <Paper sx={{ p: 3, borderRadius: 3, boxShadow: 3 }}>
              <CouponCodeGenerationStep formData={formData} updateFormData={updateFormData} readOnly={readOnly} />
            </Paper>
          </Grid>

          {/* Design Section */}
          <Grid item xs={12}>
            <Paper sx={{ p: 3, borderRadius: 3, boxShadow: 3 }}>
              <CouponDesignStep formData={formData} updateFormData={updateFormData} readOnly={readOnly} />
            </Paper>
          </Grid>

          {/* Action Buttons */}
          <Grid container justifyContent="flex-end" sx={{ mt: 2 }}>
            <Button
              color="secondary"
              variant="contained"
              onClick={handleCancel}
              sx={{ m: (theme) => theme.spacing(0, 0, 2, 2) }}
            >
              {t("coupon.cancel", "Cancel")}
            </Button>
            {!readOnly && (
              <Button
                color="primary"
                variant="contained"
                onClick={handleSave}
                disabled={saving}
                sx={{ m: (theme) => theme.spacing(0, 0, 2, 2) }}
              >
                {saving ? (
                  <>
                    <CircularProgress size={20} sx={{ mr: 1 }} color="inherit" />
                    {t("coupon.editPage.saving", "Saving...")}
                  </>
                ) : (
                  t("coupon.update", "Update")
                )}
              </Button>
            )}
          </Grid>
        </Grid>
      </Box>
    </MenuLayout>
  );
};

export default connector(CouponEditPage);
